import React, { useContext } from 'react';
import { graphql } from 'gatsby';

import { CTAConfiguration } from '../../../types/KontentProps';
import { getMediaData } from '../Global/DataUtils';
import GuidesAndManuals from './GuidesAndManuals';
import {
    GuidesAndManualsProduct,
    SupportDocument,
} from '../ProductSupportLayout/productSupportTypes';
import { extractSpecificationsImage } from '../ProductSupportLayout/productSupportUtils';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { useTaxonomy } from '../Global/DataUtils/taxonomyQueries';
import { usePageDataset } from '../../templates/PageDatasetsContext';

interface Props extends CTAConfiguration {
    kontentItemId?: string;
    product_support_items: productSupportLayoutData[];
}

export interface GuidesPageComponents {
    value: {
        __typename: string;
        elements: {
            gallery_images: {
                value: {
                    elements: {
                        file: {
                            value: {
                                url: string;
                                description: string;
                                name: string;
                            }[];
                        };
                    };
                }[];
            };
        };
    }[] &
        product_display_name_value[];
}

type product_display_name_value = {
    elements: productDetailMastHeadName & globalMastHeadName & productSpecsName;
    id?: string;
    __typename: string;
};

type productSpecsName = {
    product_name?: {
        value: string;
    };
    headline?: {
        value: string;
    };
};

type productDetailMastHeadName = {
    display_headline?: {
        value: string;
    };
};

type globalMastHeadName = {
    display_headline?: {
        value: string;
    };
};

interface extractDisplayName {
    display_page_name: {
        value: string;
    };
    page_components: {
        value: product_display_name_value[];
    };
}
interface productSupportLayoutData {
    allKontentItemComponentProductSupportLayout: {
        edges: [
            {
                node: {
                    elements: {
                        product: {
                            value: {
                                elements: {
                                    associated_product_support_page: {
                                        value: {
                                            elements: {
                                                slug: {
                                                    value: string;
                                                    url: string;
                                                };
                                            };
                                            id: string;
                                        }[];
                                    };
                                    documents: {
                                        value: SupportDocument[];
                                    };
                                };
                            }[];
                        };
                        product_0acaef2: {
                            value: {
                                id: string;
                                elements: {
                                    display_page_name: {
                                        value: string;
                                    };
                                    page_components: GuidesPageComponents;
                                };
                            }[];
                        };
                    };
                    preferred_language: string;
                };
            }
        ];
    };
}

interface GuidesQueryData {
    allKontentItemDataProductFamily: {
        edges: [
            {
                node: {
                    elements: {
                        product_family: {
                            value: { name: string; codename: string }[];
                        };
                        products: {
                            value: {
                                id: string;
                            }[];
                        };
                    };
                    preferred_language: string;
                };
            }
        ];
    };
    allKontentItemComponentProductSupportLayout: {
        edges: [
            {
                node: {
                    elements: {
                        product: {
                            value: {
                                elements: {
                                    associated_product_support_page: {
                                        value: {
                                            elements: {
                                                slug: {
                                                    value: string;
                                                    url: string;
                                                };
                                            };
                                            id: string;
                                        }[];
                                    };
                                    documents: {
                                        value: SupportDocument[];
                                    };
                                };
                            }[];
                        };
                        product_0acaef2: {
                            value: {
                                id: string;
                                elements: {
                                    display_page_name: {
                                        value: string;
                                    };
                                    page_components: GuidesPageComponents;
                                };
                            }[];
                        };
                    };
                    preferred_language: string;
                };
            }
        ];
    };
}

function mapProductFamiliesToProducts(
    guidesData: GuidesQueryData,
    productFamilyTaxonomy: any
) {
    const families: any = {};
    productFamilyTaxonomy[0].node.terms.map((term: any) => {
        const productFamilyTaxonomyCodename = term.name;
        let products: any = [];
        guidesData.allKontentItemDataProductFamily.edges.filter((edge) => {
            if (
                edge.node.elements.product_family.value[0].name ===
                productFamilyTaxonomyCodename
            ) {
                products.push(edge?.node?.elements?.products?.value);
            }
        });
        if (!productFamilyTaxonomyCodename || !products.length) return;

        if (!families[productFamilyTaxonomyCodename]) {
            families[productFamilyTaxonomyCodename] = [];
        }
        products.forEach((product: any) => {
            for (let i = 0; i < product.length; i++) {
                families[productFamilyTaxonomyCodename].push(product[i].id);
            }
        });
    });
    return families;
}

function findFamily(linkedProductPageId: string, families: any) {
    let family = '';
    for (let k in families) {
        if (families[k].includes(linkedProductPageId)) {
            family = k;
        }
    }

    return family;
}

export function extractDocuments(documents: SupportDocument[]) {
    if (!documents?.length) {
        return [];
    }

    const docs = documents.map((doc) => {
        return {
            typeCode: doc.elements?.document_type?.value[0]?.codename || '',
            typeName: doc.elements?.document_type?.value[0]?.name || '',
            name:
                doc.elements?.file?.value[0]?.description ||
                doc.system?.name ||
                '',
            image: getMediaData(doc?.elements?.image),
            pdfEmbeddedLinks: doc.elements?.pdf_embedded_links,
            url: doc.elements?.file?.value[0]?.url,
        };
    });

    return docs;
}

function getProductName(queryData: extractDisplayName) {
    var productSpecs = queryData?.page_components?.value?.filter((element) => {
        return (
            element.__typename ==
            'kontent_item_component___product_specifications'
        );
    })[0];
    var productSpecsName = productSpecs?.elements?.product_name?.value;
    if (productSpecsName) {
        return productSpecsName;
    }
    var productSpecsHeadline = productSpecs?.elements?.headline?.value;
    if (productSpecsHeadline) {
        return productSpecsHeadline;
    }

    var productMastheadDisplayName = queryData?.page_components?.value?.filter(
        (element) => {
            return (
                element.__typename ==
                'kontent_item_component___product_detail_masthead'
            );
        }
    )[0]?.elements?.display_headline?.value;

    if (!productMastheadDisplayName) {
        var globalMastheadDisplayName =
            queryData?.page_components?.value?.filter((element) => {
                return (
                    element.__typename ==
                    'kontent_item_component___global_masthead'
                );
            })[0]?.elements?.display_headline?.value;

        if (globalMastheadDisplayName == '' || !globalMastheadDisplayName) {
            return queryData?.display_page_name?.value || '';
        } else {
            return globalMastheadDisplayName;
        }
    } else {
        return productMastheadDisplayName;
    }
}

function mapProducts(
    families: any,
    productSupportLayout: any
): GuidesAndManualsProduct[] {
    const products = productSupportLayout.value.map((supportLayout: any) => {
        return {
            documentGroups:
                supportLayout?.elements?.product?.value[0]?.elements
                    ?.document_group?.value || [],
            name: getProductName(
                supportLayout?.elements?.product_0acaef2?.value[0]?.elements
            ),
            image: extractSpecificationsImage(
                supportLayout?.elements?.product_0acaef2?.value[0]?.elements
                    ?.page_components
            ),
            family: findFamily(
                supportLayout?.elements?.product_0acaef2?.value[0]?.id,
                families
            ),
            supportPage: {
                url: supportLayout?.elements?.product?.value[0]?.elements
                    ?.associated_product_support_page?.value[0]?.elements?.slug
                    ?.url,
                id: supportLayout?.elements?.product?.value[0]?.elements
                    ?.associated_product_support_page?.value[0]?.id,
            },
            documents: extractDocuments(
                supportLayout?.elements?.product?.value[0]?.elements?.documents
                    ?.value
            ),
        };
    });
    return products as GuidesAndManualsProduct[];
}

function filterCurrentLanguage(
    guidesData: GuidesQueryData,
    lang: string | undefined = 'default',
    productSupportLayout: any
): GuidesQueryData {
    return {
        allKontentItemComponentProductSupportLayout: {
            edges: guidesData?.allKontentItemComponentProductSupportLayout?.edges?.filter(
                (item) =>
                    item.node.preferred_language?.toLowerCase() ===
                    lang?.toLowerCase()
            ),
        },
        allKontentItemDataProductFamily: {
            edges: guidesData?.allKontentItemDataProductFamily?.edges.filter(
                (item) =>
                    item.node.preferred_language?.toLowerCase() ===
                    lang?.toLowerCase()
            ),
        },
    };
}
const GuidesAndManualsContainer: React.FC<Props> = (props) => {
    const pageMetadata = useContext(PageMetadataContext);
    const taxonomies = useTaxonomy();
    const productFamilyTaxonomy = taxonomies.filter(
        (taxonomyNode) => taxonomyNode.node.system.codename === 'product_family'
    );
    let guidesData: GuidesQueryData = {
        allKontentItemDataProductFamily: usePageDataset(
            'pageGuidesProductFamilies'
        ),
        allKontentItemComponentProductSupportLayout: usePageDataset(
            'pageGuidesSupportLayouts'
        ),
    };
    const productSupportLayout = props.product_support_items;
    guidesData = filterCurrentLanguage(
        guidesData,
        pageMetadata.preferredLanguage,
        productSupportLayout
    );
    const families: GuidesAndManualsProduct = mapProductFamiliesToProducts(
        guidesData,
        productFamilyTaxonomy
    );
    const allProducts = mapProducts(families, productSupportLayout);
    const familiesArray = Object.keys(families);
    return (
        <GuidesAndManuals
            families={familiesArray}
            products={allProducts}
            {...props}
        />
    );
};
export default GuidesAndManualsContainer;

export const fragmentGuidesAndManuals = graphql`
    fragment GuidesAndManuals on kontent_item_component___guides_and_manuals {
        id
        elements {
            alternate_cta_copy {
                ...kontentRichText
            }
            download_icon {
                value {
                    ... on kontent_item_media___icon {
                        id
                        elements {
                            ...kontentIcon
                        }
                    }
                }
            }
            filter_cta_label {
                value
            }
            product_support_page_cta_label {
                value
            }
            product_support_items {
                value {
                    ... on kontent_item_component___product_support_layout {
                        id
                        elements {
                            ...GuidesAndManualsSupportLayout
                        }
                        preferred_language
                    }
                }
            }
        }
    }
`;

export const fragmentGuidesAndManualsProductFamily = graphql`
    fragment GuidesAndManualsProductFamilies on kontent_item_data___product_family_elements {
        product_family {
            value {
                name
                codename
            }
        }
        products {
            value {
                ... on kontent_item_layout___master_page {
                    id
                    elements {
                        display_page_name {
                            value
                        }
                    }
                }
            }
        }
    }
`;

export const fragmentGuidesAndManualsSupportLayout = graphql`
    fragment GuidesAndManualsSupportLayout on kontent_item_component___product_support_layout_elements {
        product {
            value {
                ... on kontent_item_data___product {
                    elements {
                        document_group {
                            value {
                                ... on kontent_item_data___document_group {
                                    id
                                    elements {
                                        title {
                                            value
                                        }
                                        documents {
                                            value {
                                                ... on kontent_item_documents___products {
                                                    id
                                                    elements {
                                                        display_on_product_detail_page {
                                                            value {
                                                                codename
                                                                name
                                                            }
                                                        }
                                                        document_type {
                                                            value {
                                                                codename
                                                                name
                                                            }
                                                        }
                                                        image {
                                                            ...kontentImage
                                                        }
                                                        file {
                                                            value {
                                                                url
                                                                description
                                                            }
                                                        }
                                                        pdf_embedded_links {
                                                            ...kontentRichText
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        associated_product_support_page {
                            value {
                                ... on kontent_item_layout___master_page {
                                    elements {
                                        slug {
                                            value
                                            url
                                        }
                                    }
                                    id
                                }
                            }
                        }
                        documents {
                            name
                            value {
                                ... on kontent_item_documents___products {
                                    elements {
                                        document_type {
                                            value {
                                                codename
                                                name
                                            }
                                        }
                                        file {
                                            value {
                                                url
                                                description
                                            }
                                        }
                                        image {
                                            value {
                                                ... on kontent_item_media___image {
                                                    elements {
                                                        file {
                                                            value {
                                                                url
                                                                width
                                                                height
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        pdf_embedded_links {
                                            ...kontentRichText
                                        }
                                    }
                                    system {
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        product_0acaef2 {
            value {
                id
                ... on kontent_item_layout___master_page {
                    id
                    elements {
                        display_page_name {
                            value
                        }
                        page_components {
                            value {
                                __typename
                                ... on kontent_item_component___product_specifications {
                                    elements {
                                        product_name {
                                            value
                                        }
                                        headline {
                                            value
                                        }
                                        gallery_images {
                                            value {
                                                ... on kontent_item_media___product_image {
                                                    elements {
                                                        file {
                                                            value {
                                                                url
                                                                description
                                                                name
                                                                width
                                                                height
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                ... on kontent_item_component___product_detail_masthead {
                                    id
                                    elements {
                                        display_headline {
                                            value
                                        }
                                    }
                                }
                                ... on kontent_item_component___global_masthead {
                                    elements {
                                        display_headline {
                                            value
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
